import { useState } from "react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Cell } from "recharts";
import clsx from "clsx";
import { SlidersHorizontal } from "lucide-react";

const fuelColors = {
  hfo: "#1a5276",
  lfo: "#27ae60",
  mgo: "#d35400",
  others: "#7d3c98",
};

const fuelKeyMap = {
  "Heavy Fuel Oil": "hfo",
  "Light Fuel Oil": "lfo",
  "Marine Gas Oil": "mgo",
  "Others": "others"
};

function transformApiData(apiData) {
  const transformed = {};

  if (!Array.isArray(apiData)) {
    return transformed;
  }

  apiData.forEach(item => {
    const key = fuelKeyMap[item.name];
    if (key) {
      transformed[key] = item.value;
    }
  });

  return transformed;
}

const RemainingFuelOnboard = ({ data = [], isLoading = false }) => {
  const [isOpen, setIsOpen] = useState(true);
  const [selectedFuels, setSelectedFuels] = useState([
    "Heavy Fuel Oil",
    "Light Fuel Oil",
    "Marine Gas Oil",
    "Others",
  ]);

  const handleToggleFuel = (fuel) => {
    setSelectedFuels((prev) =>
      prev.includes(fuel) ? prev.filter((f) => f !== fuel) : [...prev, fuel]
    );
  };

  // Calculate fleet totals for each fuel type
  const fuelTypes = [
    { key: "hfo", name: "Heavy Fuel Oil" },
    { key: "lfo", name: "Light Fuel Oil" },
    { key: "mgo", name: "Marine Gas Oil" },
    { key: "others", name: "Others" },
  ];

  const transformedData = transformApiData(data);

  const fleetTotals = fuelTypes.map(fuel => ({
    name: fuel.name,
    value: transformedData[fuel.key] || 0,
    color: fuelColors[fuel.key]
  }));

  // Filter the data based on selected fuels
  const filteredData = fleetTotals.filter(item => selectedFuels.includes(item.name));

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;

    return (
      <div className="bg-white p-3 rounded-md shadow-lg border border-gray-200">
        <div className="font-semibold text-gray-800 mb-1">{payload[0].payload.name}</div>
        <div className="flex items-center">
          <div
            className="w-3 h-3 mr-2 rounded-sm"
            style={{ backgroundColor: payload[0].payload.color }}
          />
          <span className="text-sm">
            Total: <span className="font-medium">{payload[0].value} MT</span>
          </span>
        </div>
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="w-full h-[500px] rounded-3xl shadow-2xl flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-8 h-8 border-4 border-blue-500/20 border-t-blue-500 rounded-full animate-spin"></div>
          <p className="text-gray-600 font-medium">Loading engine data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full transition-all duration-300 overflow-hidden">

      <div className="flex items-center gap-4 mb-2 mt-4">
        <label className="text-sm font-semibold text-gray-700 flex items-center gap-2">
          <SlidersHorizontal className="w-4 h-4 text-[#27ade3]" />
          Select by:
        </label>
        <div className="flex flex-wrap items-center justify-between gap-1.5">
          {Object.keys(fuelKeyMap).map((fuelType) => {
            const isActive = selectedFuels.includes(fuelType);
            const fuelKey = fuelKeyMap[fuelType];

            return (
              <button
                key={fuelType}
                className={`flex items-center px-2 py-1.5 rounded-md transition-all ${isActive
                    ? "shadow-sm"
                    : "opacity-60 hover:opacity-100"
                  }`}
                style={{
                  backgroundColor: isActive ? fuelColors[fuelKey] : "transparent",
                  color: isActive ? "#ffffff" : "#6b7280"
                }}
                onClick={() => handleToggleFuel(fuelType)}
              >
                <span
                  className={`text-[13px] font-medium ${isActive ? "text-white" : "text-gray-500"
                    }`}
                >
                  {fuelType}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Chart */}
      <div className={clsx(
        "transition-all duration-500 overflow-hidden px-4 bg-white",
        isOpen ? "pt-6 max-h-[480px]" : "py-0 max-h-0"
      )}>
        <ResponsiveContainer width="100%" height={460}>
          <BarChart
            data={filteredData}
            margin={{ top: 20, right: 30, left: 20, bottom: 80 }}
            layout="horizontal"
          >
            <XAxis
              dataKey="name"
              type="category"
              tick={{ fontSize: 12 }}
              angle={0}
              textAnchor="middle"
              height={70}
            />
            <YAxis
              type="number"
              tick={{ fontSize: 12 }}
              axisLine={false}
              tickLine={false}
              unit=" MT"
            />
            <Tooltip content={<CustomTooltip />} />
            <Bar
              dataKey="value"
              name="Fuel Quantity"
              radius={[4, 4, 0, 0]}
              barSize={40}
            >
              {filteredData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default RemainingFuelOnboard;